import {Alert, SimpleGrid} from '@mantine/core';
import {useNavigate} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';
import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useProducts} from '../hooks/useProducts';
import {useProductsSearch} from '../hooks/useProductsSearch';
import {useProductsTableColumns} from '../hooks/useProductsTableColumns';
import {Product} from '../types';
import config from '../config';

// NOT APPLICABLE, on purpose:
//  - Filter drawer: status has one value (DRAFT) and listProducts takes no
//    filter argument, so there is nothing to filter on server-side. The
//    keyword search below covers name and summary.
//  - Row selection and batch actions: no batch operation exists. Drafts have
//    no delete, and publish (FNP-1961) acts on one product. When one exists,
//    add the selection column and hook together (the store slice is reserved).
export const RecordsTable = () => {

    const {columns} = useProductsTableColumns();
    const navigate = useNavigate();

    const {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        fetching,
        isError,
        nextToken,
        isFetchingMore,
        fetchRecords,
    } = useProducts();

    // The same module-scoped store the list hook filters on (FNP-815): this
    // component owns the input, useProducts owns the filtering.
    const {keyword, handleFilterByKeyword} = useProductsSearch();
    const activeKeyword = keyword.trim();

    // Two empty states, because they mean opposite things: no product exists
    // yet, or the operator's search is too narrow. A failed load says neither.
    let noRecordsText = I18n.get('No products yet. Create one with New product.');
    if (isError) {
        noRecordsText = I18n.get('No products to show');
    } else if (activeKeyword) {
        noRecordsText = `${I18n.get('No products match')} "${activeKeyword}".`;
    }

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load products')}>
                    {I18n.get('The marketplace API did not return the products. Try Reload; if it persists the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<Product>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, through
                // useTableSort in useProducts.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={fetching}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchRecords()}
                onRowClick={({record}) => navigate(`${config.basePath}/${encodeURIComponent(record.id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun='product'
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
